/**
 * Pure mapping from the `getCurrentUser` query state to a single auth status.
 *
 * This is the one source of truth for "am I signed in", derived from the
 * `/api/current_user` probe (the server's authority on the session) rather than
 * a JS-visible JWT cookie. It is a dependency-free module so both the RTK Query
 * layer (`bbkApi.slice`) and the auth utilities (`auth-utils`) can consume it
 * without creating an import cycle.
 */

import { type DefaultRootState } from 'react-redux';
import { QueryStatus } from '@reduxjs/toolkit/query';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

/**
 * Reducer key for the in-memory auth-token holder (see `auth-token.slice`).
 * Declared here, in the dependency-free auth module, so both `prepareHeaders`
 * can read the held token without importing the slice (which itself imports
 * `@reduxjs/toolkit`), keeping this module free of runtime slice dependencies.
 */
export const AUTH_TOKEN_REDUCER_PATH = 'authToken-rtk';

/**
 * The authenticity token captured by the non-caching post-login validation
 * (see `useSetBBAuthenticityToken`). It lets the company-creation POSTs during
 * sign-in send `BBK-Authenticity-Token` without populating the global
 * `getCurrentUser` query (which would flip `selectAuthStatus` to authenticated
 * mid-sign-in). Memory-only; a reload clears it and the boot probe repopulates
 * the query cache. Returns the token only when usable (length > 1).
 */
export const selectHeldAuthToken = (
  state: DefaultRootState
): string | undefined => {
  const slice = (state as Record<string, { token?: unknown } | undefined>)[
    AUTH_TOKEN_REDUCER_PATH
  ];
  const token = slice?.token;
  return typeof token === 'string' && token.length > 1 ? token : undefined;
};

/**
 * Whether an RTK Query error represents an authentication failure (401).
 * Handles both fetchBaseQuery error shapes: a parsed JSON error (`status`) and
 * a parse failure that surfaces the HTTP code as `originalStatus`.
 */
export const isUnauthorizedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const e = error as { status?: unknown; originalStatus?: unknown };
  return e.status === 401 || e.originalStatus === 401;
};

/**
 * The single definition of "this current-user response can drive an
 * authenticated session": it carries an authenticity token usable for the
 * `BBK-Authenticity-Token` header. Shared by the auth-status mapper, the reauth
 * resolver, and header preparation so those three no longer disagree (a
 * tokenless 200 must not read as authenticated in one place while the resolver
 * rejects it in another).
 */
export const hasUsableAuthenticityToken = (
  user: unknown
): user is { authenticity_token: string } => {
  if (!user || typeof user !== 'object') {
    return false;
  }
  const token = (user as { authenticity_token?: unknown }).authenticity_token;
  return typeof token === 'string' && token.length > 1;
};

type CurrentUserQueryState = {
  status: QueryStatus;
  data?: unknown;
  error?: unknown;
};

/**
 * Map the current-user query state to an auth status.
 *
 * A mid-session 401 is handled as a hard browser session boundary (the
 * unauthorized-redirect middleware reloads to sign-in), so this pure snapshot
 * does not need a durable "was invalidated" marker. Rules:
 * - a 401 in the current query state is authoritative on any non-`fulfilled`
 *   status: RTK retains both the stale `data` and the `error` across a rejected
 *   refetch AND the following pending retry, only clearing `error` on a fresh
 *   `fulfilled`. Keying off the error (not the transient status) keeps a 401
 *   unauthenticated through the retry rather than flipping back on stale data;
 * - otherwise a present user with a usable authenticity token is authenticated
 *   (so a pending refetch or a transient/non-401 error keeps the last
 *   known-good session); a tokenless user cannot make scoped calls, so it does
 *   not count as authenticated;
 * - an in-flight probe with no usable data yet is loading;
 * - anything else settled without a usable user is unauthenticated.
 */
export const authStatusFromQuery = ({
  status,
  data,
  error,
}: CurrentUserQueryState): AuthStatus => {
  if (status !== QueryStatus.fulfilled && isUnauthorizedError(error)) {
    return 'unauthenticated';
  }
  if (data && hasUsableAuthenticityToken(data)) {
    return 'authenticated';
  }
  if (status === QueryStatus.uninitialized || status === QueryStatus.pending) {
    return 'loading';
  }
  return 'unauthenticated';
};

/**
 * What the probe can tell us about the session, for callers that act on its
 * absence rather than just render differently.
 *
 * `authStatusFromQuery` folds "the server says you are signed out" and "the
 * server could not be reached" into `unauthenticated`, which is right for
 * rendering — an unreachable server cannot show authenticated UI either. It is
 * wrong for a decision to stop treating a session as real, because a momentary
 * 5xx would then read as proof that nothing is there. Only a 401 is that proof.
 */
export type SessionProbe = 'present' | 'absent' | 'unknown' | 'pending';

/**
 * Map the current-user query state to session evidence, in the same precedence
 * order as `authStatusFromQuery` so the two cannot disagree about which signal
 * wins: a 401 outranks stale data, and usable data outranks a transient error.
 */
export const sessionProbeFromQuery = ({
  status,
  data,
  error,
}: CurrentUserQueryState): SessionProbe => {
  if (status !== QueryStatus.fulfilled && isUnauthorizedError(error)) {
    return 'absent';
  }
  if (data && hasUsableAuthenticityToken(data)) {
    return 'present';
  }
  if (status === QueryStatus.uninitialized || status === QueryStatus.pending) {
    return 'pending';
  }
  // Settled without a usable user: a non-401 failure leaves the session
  // undecided, while a clean response is authoritative that there is none.
  return error ? 'unknown' : 'absent';
};
